import React, {FC, useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import useAppToast from 'src/hooks/useAppToast'
import {Platform, RequirementItem} from 'src/interfaces/Platform.interface'
import {handleOauthForConnectPlatformAsync} from 'src/redux/actions/authActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {handleRoutePathOnOauth, shopifyDetailsForOauth} from 'src/utils/constants'
import InputFieldComponent from './InputFieldComponent'
import ConnectPlatformSelectInput from './ConnectPlatformSelectInput'
import AuthRequirementSelectInput from './AuthRequirementSelectInput'
import { fetchCountriesAsync, fetchTemuCountryAsync } from 'src/redux/actions/accountActions'
import { getLogicBrokerPartnersAsync } from 'src/redux/actions/platformActions'
import {
  ConnectionNotice,
  SHIPPHQ_ADMIN_PORTAL_CONNECT_MESSAGE,
  buildConnectionChannelState,
  deriveOAuthStoreFlags,
  isShippHqAdminPortalOnlyPlatform,
} from 'src/utils/platformConnectionValidation'

type ConnectPlatformModalPropsType = {
  isVisible: boolean
  isPrimary?: boolean
  connectNature?: string
  connectionNotice?: ConnectionNotice | null
  closeModal: () => void
  data: Platform | null
  // Add a function to handle received data from child component
  onDataReceived: (data: any, friendlyName: string,connection_type?: string, extras?: Record<string, any>) => void
  // onUpdateState: (newValue: boolean) => void
}
type dataCollectorType = {
  id: number
  name: string
  value: string
   connection_type?: any
}

const getCountryListFromPayload = (payload: any) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results?.data)) return payload.results.data
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const mapCountryOptions = (payload: any) =>
  getCountryListFromPayload(payload)
    .map((country: any) => {
      const name = country?.name ?? country?.country ?? country?.label
      const code =
        country?.country_iso ?? country?.code ?? country?.country_code ?? country?.value
      return name ? {label: name, value: code ?? name} : null
    })
    .filter((option: {label: string; value: string} | null): option is {label: string; value: string} =>
      Boolean(option)
    )

const ConnectPlatformModal: FC<ConnectPlatformModalPropsType> = ({
  isVisible,
  data,
  isPrimary = false,
  connectNature,
  connectionNotice,
  closeModal,
  onDataReceived,
}) => {
  const [inputData, setInputData] = useState({
    friendlyName: '',
    intput_type: '', // State to store the value of "Friendly Name"
    connection_type: 'api_key',
    country: '',
    country_code: '',
    country_iso: '',
    country_region: '',
    Region: '',
  })
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const { temuCountryData } = useAppSelector((state) => state.fetchTemuCountry);
  const {
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
  } = useAppSelector((state) => state.fetchConnectedPlatform)
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const {successToast, warningToast} = useAppToast()
  const {platformSettingsLoading} = useAppSelector((state) => state.createPlatformSettings)
  const {platformData, logicBrokerPartnerLoading, logicBrokerPartnerData} = useAppSelector(
    (state) => state.platform
  )
  const [isConnectingUsingOauth, setIsConnectingUsingOauth] = useState<boolean>(data?.isOauth ?? false )
  const shouldPreferApiKeyConnection = ['WOOCOMMERCE', 'WALMART'].includes(data?.name ?? '')
  const is_oauth_recommended =
    data?.name === 'WALMART'
      ? '(Recommended only for US marketplaces)'
      : shouldPreferApiKeyConnection
      ? ''
      : '(Recommended)'
  const is_api_key_recommended = shouldPreferApiKeyConnection ? '(Recommended)' : ''
  const supportsConnectionTypeToggle = Boolean(data?.isAllowMultiAuth || data?.name === 'WALMART')
  const canSubmitViaOauth = Boolean(data?.isOauth || data?.name === 'WALMART')
  const [domainField, setDomainField] = useState(false)
  const [logicBrokerCompanyName, setLogicBrokerCompanyName] = useState('')
  const [logicBrokerPartnerId, setLogicBrokerPartnerId] = useState('')
  const [lastLogicBrokerLookup, setLastLogicBrokerLookup] = useState({key: '', accountType: ''})
  const [countryNameOptions, setCountryNameOptions] = useState<{label: string; value: string}[]>([])
  const [checkState, setCheckState] = useState({
    oauth: false,
    apiKey: false,
  });
  // const handleClick = () => {
  //   onUpdateState(true) // Call callback function to update parent state
  //   console.log('true', onUpdateState)
  // }

  const [datacollector, setDataCollector] = useState<dataCollectorType[] | []>([]) // Array to store collected data
  const currentPlatformName = data?.name ?? ''
  const normalizedPlatformName = currentPlatformName.toUpperCase()
  const currencyPlatforms = ['FARFETCH']
  const accountModePlatforms = ['ALLEGRO', 'LOGICBROKER']
  const authRequirements: RequirementItem[] =
    data?.available_platform_auth_requirement ?? []

  const matchesPlatform = (...platforms: string[]) =>
    platforms.some((platform) => normalizedPlatformName === platform.toUpperCase())
  const isShippHqAdminPortalOnly = isShippHqAdminPortalOnlyPlatform(data?.name)

  const shouldHideAuthRequirement = (requirement: RequirementItem) => {
    const normalizedName = (requirement.name ?? '').toLowerCase()
    const normalizedDisplay = (requirement.display_name ?? '').toLowerCase()
    const hasAccount = normalizedName.includes('account') || normalizedDisplay.includes('account')
    const hasMode = normalizedName.includes('mode') || normalizedDisplay.includes('mode')
    if (matchesPlatform('EBAY')) {
      const hasCurrency =
        normalizedName.includes('currency') || normalizedDisplay.includes('currency')
      if (hasCurrency) return true
      return hasAccount && hasMode
    }
    if (!matchesPlatform('SHOPWARE')) return false
    return hasAccount && hasMode
  }

  const visibleAuthRequirements = authRequirements.filter(
    (requirement) => !shouldHideAuthRequirement(requirement)
  )
  const shouldCollectAuthRequirementsBeforeOauth =
    visibleAuthRequirements.length > 0 &&
    Boolean(data?.isOauth) &&
    !supportsConnectionTypeToggle

  const hasCurrencyRequirement =
    matchesPlatform(...currencyPlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('currency') || normalizedDisplay.includes('currency')
    })

  const hasAccountModeRequirement =
    matchesPlatform(...accountModePlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      const hasAccount = normalizedName.includes('account') || normalizedDisplay.includes('account')
      const hasMode = normalizedName.includes('mode') || normalizedDisplay.includes('mode')
      return hasAccount && hasMode
    })

  const shouldRenderCurrencyFallback =
    matchesPlatform(...currencyPlatforms) && !hasCurrencyRequirement
  const shouldRenderAccountModeFallback =
    matchesPlatform(...accountModePlatforms) && !hasAccountModeRequirement
  const hasCloverRegionRequirement =
    matchesPlatform('CLOVER') &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('region') || normalizedDisplay.includes('region')
    })
  const shouldRenderCloverRegion = matchesPlatform('CLOVER') && !hasCloverRegionRequirement
  const countryFallbackPlatforms = ['NOONV2', 'FRUUGOV2']
  const hasCountryRequirement =
    matchesPlatform(...countryFallbackPlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('country') || normalizedDisplay.includes('country')
    })
  const amazonSellerCountryRequirement = platformData
    ?.find((platform) => platform.name === 'AMAZONSELLER')
    ?.available_platform_auth_requirement?.find(({name, display_name, input_type}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return (
        input_type === 'select' &&
        (normalizedName.includes('country') || normalizedDisplay.includes('country'))
      )
    }) ?? {id: 190, input_type: 'select', name: 'country', display_name: 'Country Name'}
  const shouldRenderCountryFallback =
    matchesPlatform(...countryFallbackPlatforms) && !hasCountryRequirement

  const shouldRenderEbayCountrySelector = matchesPlatform('EBAY')

  const cloverRegionOptions = [
    {label: 'North America: US and Canada (api.clover.com)', value: 'US'},
    {label: 'Europe (api.eu.clover.com)', value: 'EU'},
    {label: 'Latin America (api.la.clover.com)', value: 'LA'},
  ]
  const noonV2CountryOptions = [
    {label: 'Saudi Arabia (KSA)', value: 'SA'},
    {label: 'United Arab Emirates (UAE)', value: 'AE'},
  ]
// Set default checked values
useEffect(() => {
  setCheckState({
    oauth: is_oauth_recommended === '(Recommended)',
    apiKey: is_api_key_recommended === '(Recommended)',
  });
  if(['WOOCOMMERCE', 'WALMART'].includes(data?.name ?? ''))setIsConnectingUsingOauth(false)
}, []);

const handleRadioChange = (type: 'oauth' | 'apiKey') => (ev: React.ChangeEvent<HTMLInputElement>) => {
  setCheckState({
    oauth: type === 'oauth' ? ev.target.checked : !ev.target.checked,
    apiKey: type === 'apiKey' ? ev.target.checked : !ev.target.checked,
  });

  if (type === 'oauth' && !isConnectingUsingOauth) {
    setIsConnectingUsingOauth(ev.target.checked);
  } else if (type === 'apiKey' && isConnectingUsingOauth) {
    setIsConnectingUsingOauth(!ev.target.checked);
  }
};

  const handleInputChange = (id: any, name: string, value: string) => {
    setInputData({...inputData, [id]: value, [name]: value})
    if(name === 'proxies') {
      value === 'SANDBOX' ? setDomainField(true) : setDomainField(false)
    }
  }

  const handleFruugoV2CountryChange = (countryCode: string) => {
    const selectedCountry = countryNameOptions.find((country) => country.value === countryCode)
    setInputData({
      ...inputData,
      country: selectedCountry?.label ?? '',
      country_code: countryCode,
    })
  }

  const isValueEmpty = (value: any) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')

  const getRequirementValue = (...keys: string[]) => {
    for (const key of keys) {
      const directValue = (inputData as Record<string, any>)[key]
      if (!isValueEmpty(directValue)) {
        return directValue
      }
    }

    const normalizedKeys = keys.map((key) => key.toLowerCase())
    const matchedRequirement = data?.available_platform_auth_requirement?.find(
      ({name, display_name}) => {
        const normalizedName = (name ?? '').toLowerCase()
        const normalizedDisplay = (display_name ?? '').toLowerCase()
        return normalizedKeys.some((key) => {
          return (
            normalizedName.includes(key) ||
            key.includes(normalizedName) ||
            normalizedDisplay.includes(key) ||
            key.includes(normalizedDisplay)
          )
        })
      }
    )

    if (matchedRequirement) {
      const candidates = [
        matchedRequirement.name,
        matchedRequirement.display_name,
        String(matchedRequirement.id),
      ].filter(Boolean) as string[]

      for (const candidate of candidates) {
        const value = (inputData as Record<string, any>)[candidate]
        if (!isValueEmpty(value)) {
          return value
        }
      }
    }

    return undefined
  }

  const getLogicBrokerLookupInputs = () => {
    const subscriptionKey = String(
      getRequirementValue('subscription_key', 'subscription key', 'key', 'client_secret') ?? ''
    ).trim()
    const accountMode = String(getRequirementValue('account_mode', 'account mode', 'mode') ?? '')
      .trim()
      .toLowerCase()
    return {subscriptionKey, accountMode}
  }

  const fetchLogicBrokerPartner = async (force = false) => {
    if (!matchesPlatform('LOGICBROKER')) {
      return null
    }

    const {subscriptionKey, accountMode} = getLogicBrokerLookupInputs()
    if (!subscriptionKey || !accountMode) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return null
    }

    if (
      !force &&
      lastLogicBrokerLookup.key === subscriptionKey &&
      lastLogicBrokerLookup.accountType === accountMode &&
      logicBrokerPartnerId
    ) {
      return {partnerId: logicBrokerPartnerId, companyName: logicBrokerCompanyName}
    }

    setLastLogicBrokerLookup({key: subscriptionKey, accountType: accountMode})
    await dispatch(getLogicBrokerPartnersAsync({account_type: accountMode, key: subscriptionKey}))
    if (force && !logicBrokerPartnerData?.partnerId) {
      warningToast('Unable to resolve LogicBroker company for provided values')
      return null
    }
    return {
      partnerId: logicBrokerPartnerData?.partnerId || '',
      companyName: logicBrokerPartnerData?.companyName || '',
    }
  }

  const handleSave = async () => {
    if (matchesPlatform('LOGICBROKER')) {
      if (logicBrokerPartnerLoading) {
        warningToast('Please wait while LogicBroker company is being fetched')
        return
      }
      if (!logicBrokerPartnerId) {
        warningToast('Please select Account Mode and wait for company details')
        return
      }
    }

    if (matchesPlatform('FARFETCH')) {
      const currencyValue = getRequirementValue('currency', 'select currency')
      if (isValueEmpty(currencyValue)) {
        warningToast('Currency is required')
        return
      }
    }

    if (matchesPlatform('EBAY')) {
      const countryIsoValue = getRequirementValue('country_iso')
      if (isValueEmpty(countryIsoValue)) {
        warningToast('Country is required')
        return
      }
    }

    if (matchesPlatform(...accountModePlatforms)) {
      const accountModeValue = getRequirementValue('account_mode', 'account mode', 'mode')
      if (isValueEmpty(accountModeValue)) {
        warningToast('Account Mode is required')
        return
      }
    }

    const cloverRegionValue = getRequirementValue('region', 'Region', 'country_region')
    if (matchesPlatform('CLOVER')) {
      if (isValueEmpty(cloverRegionValue)) {
        warningToast('Region is required')
        return
      }
    }
    if (matchesPlatform(...countryFallbackPlatforms)) {
      const countryValue = getRequirementValue('country')
      if (isValueEmpty(countryValue)) {
        warningToast('Country is required')
        return
      }
    }

    // Collect data from inputData and update dataCollector state
    const collectedData = visibleAuthRequirements.length
      ? visibleAuthRequirements.map(({id, name}) => ({
          id,
          value: inputData[id],
          name: name,
        }))
      : []
    if (matchesPlatform('CLOVER') && !isValueEmpty(cloverRegionValue)) {
      const regionIndex = collectedData.findIndex(({name}) =>
        ['region', 'country_region'].includes((name ?? '').toLowerCase())
      )

      if (regionIndex >= 0) {
        collectedData[regionIndex] = {
          ...collectedData[regionIndex],
          name: 'country_region',
          value: cloverRegionValue,
        }
      } else {
        collectedData.push({
          id: -102,
          value: cloverRegionValue,
          name: 'country_region',
        })
      }
    }
    if (matchesPlatform(...countryFallbackPlatforms)) {
      const countryValue = getRequirementValue('country')
      if (!isValueEmpty(countryValue)) {
        const countryCodeValue = getRequirementValue('country_code')
        const countryIndex = collectedData.findIndex(
          ({name}) => (name ?? '').toLowerCase() === 'country'
        )
        if (countryIndex >= 0) {
          collectedData[countryIndex] = {
            ...collectedData[countryIndex],
            name: 'country',
            value: countryValue,
          }
        } else {
          collectedData.push({
            id: -103,
            value: countryValue,
            name: 'country',
          })
        }
        if (matchesPlatform('FRUUGOV2') && !isValueEmpty(countryCodeValue)) {
          const countryCodeIndex = collectedData.findIndex(
            ({name}) => (name ?? '').toLowerCase() === 'country_code'
          )
          if (countryCodeIndex >= 0) {
            collectedData[countryCodeIndex] = {
              ...collectedData[countryCodeIndex],
              name: 'country_code',
              value: countryCodeValue,
            }
          } else {
            collectedData.push({
              id: -104,
              value: countryCodeValue,
              name: 'country_code',
            })
          }
        }
      }
    }
    if (matchesPlatform('EBAY')) {
      const countryIsoValue = getRequirementValue('country_iso')
      if (!isValueEmpty(countryIsoValue)) {
        const countryIsoIndex = collectedData.findIndex(
          ({name}) => (name ?? '').toLowerCase() === 'country_iso'
        )
        if (countryIsoIndex >= 0) {
          collectedData[countryIsoIndex] = {
            ...collectedData[countryIsoIndex],
            name: 'country_iso',
            value: countryIsoValue,
          }
        } else {
          collectedData.push({
            id: -106,
            value: countryIsoValue,
            name: 'country_iso',
          })
        }
      }
    }
    if (matchesPlatform(...accountModePlatforms)) {
      const accountModeValue = getRequirementValue('account_mode', 'account mode', 'mode')
      if (!isValueEmpty(accountModeValue)) {
        const hasAccountModeEntry = collectedData.some(({name}) => {
          const normalizedName = (name ?? '').toLowerCase()
          const hasAccount = normalizedName.includes('account')
          const hasMode = normalizedName.includes('mode')
          return hasAccount && hasMode
        })
        if (!hasAccountModeEntry) {
          collectedData.push({
            id: 'account_mode' as any,
            value: accountModeValue,
            name: 'account_mode',
          })
        }
      }
    }
    if (matchesPlatform('LOGICBROKER') && logicBrokerPartnerId) {
      let hasClientIdEntry = false
      collectedData.forEach((item) => {
        if ((item.name ?? '').toLowerCase() === 'client_id') {
          item.value = logicBrokerPartnerId
          hasClientIdEntry = true
        }
      })
      if (!hasClientIdEntry) {
        collectedData.push({
          id: 'client_id' as any,
          value: logicBrokerPartnerId,
          name: 'client_id',
        })
      }
    }
    setDataCollector(collectedData)
      //Create default channel name, if user dont add any name
      if(inputData['friendlyName']==''){
        const count = fetchedConnectedPlatformData?.filter(item => item.name === data?.name).length;
      const value = count > 0 ? `${data?.name}_${count}` : data?.name;
      if(value) inputData['friendlyName'] = value
      }
    onDataReceived(collectedData, inputData.friendlyName,inputData.connection_type)
    // closeModal() // Close modal after saving
  }

  const handleOauth = () => {
    console.log(inputData, inputData['identifier'])
    const payload = {available_platform_name: data?.name,  user_id: fetchedUserAccountData?.id }

    //Create default channel name, if user dont add any name
    if(inputData['friendlyName']==''){
      const count = fetchedConnectedPlatformData?.filter(item => item.name === data?.name).length;
      const value = count > 0 ? `${data?.name}_${count}` : data?.name;
      if(value) inputData['friendlyName'] = value
    }

    if (matchesPlatform('FARFETCH')) {
      const currencyValue = getRequirementValue('currency', 'select currency')
      if (isValueEmpty(currencyValue)) {
        warningToast('Currency is required')
        return
      }
      payload['currency'] = currencyValue
    }

    if (matchesPlatform('EBAY')) {
      const countryIsoValue = getRequirementValue('country_iso')
      if (isValueEmpty(countryIsoValue)) {
        warningToast('Country is required')
        return
      }
      payload['country_iso'] = countryIsoValue
    }

    if (matchesPlatform(...accountModePlatforms)) {
      const accountModeValue = getRequirementValue('account_mode', 'account mode', 'mode')
      if (isValueEmpty(accountModeValue)) {
        warningToast('Account Mode is required')
        return
      }
      payload['account_mode'] = accountModeValue
    }
    if (matchesPlatform('CLOVER')) {
      const regionValue = getRequirementValue('region', 'Region', 'country_region')
      if (isValueEmpty(regionValue)) {
        warningToast('Region is required')
        return
      }
      payload['country_region'] = regionValue
    }

    if (data?.name === 'SHOPIFY') {
      if (inputData['identifier'] == '' || inputData['identifier'] === undefined) {
        warningToast('Domain is required')
        return
      } else {
        payload['shop'] = inputData['identifier']
      }
    }

    if (data?.name === 'ZohoInventory') {
      if (inputData['country'] == '' || inputData['country'] === undefined) {
        warningToast('Country is required')
        return
      } else {
        payload['country'] = inputData['country']
      }
    }

    if(data?.name==='AMAZONMCF'){
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    if (data?.name === 'TIKTOK') {
      if (inputData['country']) {
        payload['country'] = inputData['country']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if(data?.name==='AIMS360'){
      if(inputData['proxies']){
        payload['proxies'] = inputData['proxies']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    if(data?.name==='TEMU'){
      if(inputData['country']){
        payload['country'] = inputData['country']
      }else{
        warningToast("Country is required")
        return 
      }
    }
    
    if(data?.name==='NOON' ){
      if(inputData['token_secret']){
        payload['token_secret'] = inputData['token_secret']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    if(data?.name==='ACUMATICA'){
      if (!inputData['identifier']) {
        warningToast('Domain is required');
        return;
      }
      if (!inputData['proxies']) {
        warningToast('Proxies is required');
        return;
      }
      if (!inputData['client_id']) {
        warningToast('Client ID is required');
        return;
      }
      if (!inputData['client_secret']) {
        warningToast('Client Secret is required');
        return;
      }
      payload['proxies'] = inputData['proxies']
      payload['identifier'] = inputData['identifier']
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      payload['version'] = inputData['version']
    }

    if(data?.name==='WOOCOMMERCE'){
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Domain name is required")
        return 
      }
    }

    if(data?.name==='ZALORA' ){
      if(inputData['proxies']){
        payload['proxies'] = inputData['proxies']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    // if(data?.name==='AMAZONVENDOR' ){
    //   if(inputData['proxies']){
    //     payload['proxies'] = inputData['proxies']
    //   }else{
    //     warningToast("Selling Party Id is required")
    //     return 
    //   }
    // }

    if(data?.name==='CDON' || data?.name==='BOL'){
      if(inputData['country_code']){
        payload['country_code'] = inputData['country_code']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    if(data?.name==='AMAZONSELLER' || data?.name==='WALMART' || data?.name === 'AMAZONVENDOR'){
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Country is required")
        return 
      }
    }

    if(data?.name==='SQUARE' || data?.name === 'LIGHTSPEED' || data?.name==='CIN7OMNI'){
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Currency is required")
        return 
      }
    }

    if(data?.name==='SHOPEE'){
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Country is required")
        return 
      }
       if(inputData['proxies']){
        payload['proxies'] = inputData['proxies']
      }else{
        warningToast("Seller Type is required")
        return 
      }

      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']     
    }

    if(data?.name==='LAZADA'){
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Country is required")
        return 
      }
    }
      if(data?.name==='TRADEME'){
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      if(inputData['identifier']){
        payload['identifier'] = inputData['identifier']
      }else{
        warningToast("Currency is required")
        return 
      }
    }

    if(data?.name==='JUMIAVENDOR'){
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
    }

    if (shouldCollectAuthRequirementsBeforeOauth) {
      for (const requirement of visibleAuthRequirements) {
        const fieldName = requirement.name ?? ''
        const value =
          getRequirementValue(fieldName, requirement.display_name ?? '') ??
          (fieldName ? inputData[fieldName] : undefined)
        if (isValueEmpty(value)) {
          warningToast(`${requirement.display_name || fieldName || 'Required field'} is required`)
          return
        }
        if (fieldName) {
          payload[fieldName] = value
        }
      }
    }

    setLoading(true)
    dispatch(handleOauthForConnectPlatformAsync({...payload})).then((response) => {
      const channelState = buildConnectionChannelState({
        mainStoreData,
        accountingStoreData,
        shippingStoreData,
        erpStoreData,
        omsStoreData,
        ticketStoreData,
        fetchedConnectedPlatformData,
      })
      const resolvedNature = connectNature ?? (isPrimary ? 'PRIMARY' : 'SECONDARY')
      const storeFlags = deriveOAuthStoreFlags(data, resolvedNature, isPrimary, channelState)
      if (response.payload?.data && response.payload?.status_code === 200) {
        localStorage.setItem(
          shopifyDetailsForOauth,
          JSON.stringify({
            ...data,
            friendlyName: inputData.friendlyName,
            isMainStore: storeFlags.isMainStore,
            isAccountingStore: storeFlags.isAccountingStore,
            isShippingStore: storeFlags.isShippingStore,
            isErpStore: storeFlags.isErpStore,
            isOmsStore: storeFlags.isOmsStore,
            isTicketStore: storeFlags.isTicketStore,
            userId: fetchedUserAccountData?.id,
            zohoAuthDomain: inputData['identifier'] ? inputData['identifier'] : '',
            client_id:inputData['client_id'], client_secret:inputData['client_secret'], proxies:inputData['proxies'], version:inputData['version'], ...(matchesPlatform('EBAY') ? {country_iso: inputData['country_iso']} : {country: Number(inputData['country'])})
          })
        )
        localStorage.setItem(handleRoutePathOnOauth, 'true')
        setLoading(false)
        window.open(response.payload?.data?.url, '_self')
        // window.open(response.payload?.data?.url,"_blank",'height=400,width=400,top=100,left=300')
        // window.location.href = response.payload?.data?.url
      }
      if (response.payload?.data?.status_code == 400) {
        warningToast(response.payload?.data?.data?.message)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
      if(data?.name === 'TEMU') dispatch (fetchTemuCountryAsync())
    }, [data?.name])

   useEffect(() => {
      if (!matchesPlatform('FRUUGOV2', 'EBAY')) return
      dispatch(fetchCountriesAsync() as any)
        .then((response: any) => {
          setCountryNameOptions(mapCountryOptions(response?.payload))
        })
        .catch(() => setCountryNameOptions([]))
    }, [dispatch, normalizedPlatformName])

  useEffect(() => {
    if (!matchesPlatform('LOGICBROKER')) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      setLastLogicBrokerLookup({key: '', accountType: ''})
      return
    }

    const {subscriptionKey, accountMode} = getLogicBrokerLookupInputs()
    if (!subscriptionKey || !accountMode) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return
    }

    setLogicBrokerCompanyName('')
    setLogicBrokerPartnerId('')
    const timer = setTimeout(() => {
      fetchLogicBrokerPartner(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [
    data?.name,
    inputData['subscription_key'],
    inputData['key'],
    inputData['client_secret'],
    inputData['account_mode'],
    inputData['mode'],
  ])

  useEffect(() => {
    if (!matchesPlatform('LOGICBROKER')) {
      return
    }
    if (!logicBrokerPartnerData) {
      return
    }
    if (
      logicBrokerPartnerData.key !== lastLogicBrokerLookup.key ||
      logicBrokerPartnerData.accountType !== lastLogicBrokerLookup.accountType
    ) {
      return
    }
    if (!logicBrokerPartnerData.partnerId) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return
    }
    setLogicBrokerCompanyName(logicBrokerPartnerData.companyName)
    setLogicBrokerPartnerId(logicBrokerPartnerData.partnerId)
    setInputData((prev) => ({...prev, client_id: logicBrokerPartnerData.partnerId}))
  }, [logicBrokerPartnerData, lastLogicBrokerLookup.key, lastLogicBrokerLookup.accountType, data?.name])

  return (
    <>
      {/* <Modal
        size='lg'
        backdrop='static'
        backdropClassName='customBackdropOfModal'
        className='customBackdropOfModal'
        centered
        show={isVisible}
        onHide={() => {
          closeModal()
          setInputData({
            friendlyName: '',
            intput_type: '',
            connection_type: '',
            country: '',
            country_code: '',
            country_region: '',
            Region: '',
          })
        }}
      > */}
        <form onSubmit={(ev)=>{
          ev.preventDefault()
          if (isShippHqAdminPortalOnly) {
            return
          }
          if(!canSubmitViaOauth || isConnectingUsingOauth ===false){
            // alert('without oauth')
            handleSave()
          }else{
            // alert('with oauth')
            handleOauth()
          }
        }} >

          {connectionNotice?.message ? (
            <div className={`alert alert-${connectionNotice.type} py-3 mb-4 mx-2`}>
              {connectionNotice.message}
            </div>
          ) : null}

          {/* only for presta shop connection type  */}

   {  data?.name=='PRESTASHOP' ?  
        <>
          <div className='row  pe-0 py-2' >
            <div className='col-md-3 col-4' >
              <label className='form-label mb-0' >Connection Type</label>
            </div>
            <div className='col-sm-9 col-12 ps-0' style={{alignContent:'space-evenly'}} >
              <div className='d-flex align-items-center'  > 
                <label htmlFor="default" className=' ms-2' ><input id='default' checked={inputData?.connection_type == "api_key"} className='form-check-input mx-1' name='prestaconnectionType' type='radio' onChange={()=> {handleInputChange("default","connection_type",'api_key')}}/>Default (Recommended)</label>
                <label htmlFor="custom" className=' me-2' ><input id='custom' checked={inputData?.connection_type == "ws_key"} className='form-check-input form-check-input-lg mx-1' name='prestaconnectionType' type='radio' onChange={()=> {handleInputChange("custom","connection_type",'ws_key')}}/>Custom (for Self-hosted & older version setup)  </label>
              </div>
            </div>
          </div>
          <div className='separator my-1' ></div>
        </> : null }
        
        {  supportsConnectionTypeToggle ?  
        <>
          <div className='row py-2' >
            <div className='col-md-3 col-6' >
              <label className='form-label mb-0' >Connection Type</label>
            </div>
            <div className='col-sm-9 col-12' style={{alignContent:'space-evenly'}} >
              <div className='d-flex align-items-center'  > 
                <label htmlFor="withoutOauth" className=' ms-2' ><input id='withoutOauth' checked={checkState.apiKey} className='form-check-input mx-1' name='checkOauthType' type='radio' onChange={handleRadioChange('apiKey')}/>Via API Keys {is_api_key_recommended}</label>
                <label htmlFor="withOauth" className=' me-2' ><input id='withOauth' checked={checkState.oauth} className='form-check-input form-check-input-lg mx-1' name='checkOauthType' type='radio' onChange={handleRadioChange('oauth')} />Secure Connection via OAuth {is_oauth_recommended}</label>
              </div>
            </div>
          </div>
          <div className='separator my-1' ></div>
        </> : null }
            { data?.auth_route_name === "chrome-extension" ? (
                <>
                <div className="d-flex justify-content-center align-items-center text-secondary" style={{ height: "10vh" }}>
                <h5>Go to Commercium Extension to connect {data?.display_name}</h5>
                </div>
                  <div className='text-end mx-2 my-2'><Button disabled={loading} type='button' className='btn-sm' variant='secondary' onClick={()=>{
                    closeModal()
                    setInputData({
                      friendlyName:'',
                      intput_type:'',
                      connection_type:'',
                      country: '',
                      country_code: '',
                      country_iso: '',
                      country_region: '',
                      Region: '',
                    })
                    setLogicBrokerCompanyName('')
                    setLogicBrokerPartnerId('')
                    setLastLogicBrokerLookup({key: '', accountType: ''})
                  }}>
                    Close
                  </Button></div>
                 </>
              ) : 
          (<>
          <div className='mt-4'>
            {isShippHqAdminPortalOnly ? (
              <div className='alert alert-info py-3 mb-0 mx-2'>{SHIPPHQ_ADMIN_PORTAL_CONNECT_MESSAGE}</div>
            ) : (
              <>
            {visibleAuthRequirements.length > 0 ||
            data?.isOauth ||
            shouldRenderCloverRegion ||
            shouldRenderCountryFallback ||
            shouldRenderEbayCountrySelector ? (
              <div className='row mb-3'>
                <div className='col-md-3 col-6 '>
                  <label
                    className='form-label'
                    // style={{
                    //   marginRight: '10px', // Adjust as needed
                    //   fontWeight: 'bold',
                    //   fontSize: '1.25rem', // Adjust as needed
                    // }}
                  >
                    Channel Name
                  </label>
                </div>
                <div className='col-sm-6 col-12'>
                  {' '}
                  {/* Apply col-md-6 for half column width */}
                  <input
                    type='text'
                    // required
                    className='form-control form-control-solid'
                    placeholder='Just enter any name for your reference to this channel'
                    value={inputData.friendlyName}
                    onChange={(e) =>
                      handleInputChange('friendlyName', 'friendlyName', e.target.value)
                    }
                  />
                </div>
              </div>
            ) : null}
            {(visibleAuthRequirements.length > 0 ||
              shouldRenderCurrencyFallback ||
              shouldRenderAccountModeFallback ||
              shouldRenderCloverRegion ||
              shouldRenderCountryFallback ||
              shouldRenderEbayCountrySelector) && (
              <>
                
              <div className='row'>
                    {visibleAuthRequirements.length > 0 &&
                      (!canSubmitViaOauth ||
                      isConnectingUsingOauth === false ||
                      shouldCollectAuthRequirementsBeforeOauth
                        ? visibleAuthRequirements.map(
                            (item: RequirementItem, index) => {
                            if(data?.name === 'TEMU'){
                              if(item.input_type=='select'){
                                return(
                                  <div key={item.id} className={`mb-3`}>
                                    <ConnectPlatformSelectInput
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                      onDataChange={(ev) =>{
                                        handleInputChange( item.id , item.name, ev.target.value )
                                      }}
                                      options={item.display_name == "Select Region" ? [{ label: 'US', value: 'US' }, { label: 'EU', value: "EU" }, { label: 'GLOBAL', value: "GLOBAL" }] :
                                  temuCountryData.data?.map((data: any) => ({
                                    value: data.region_id,
                                    label: data.country_name as any,
                                  })) || []
                                }
                                    />
                                  </div>
                                )
                              }
                            }

                            if(data?.name === 'AIMS360'){
                              if(item.input_type=='select'){
                                return(
                                  <div key={item.id} className={`mb-3`}>
                                    <ConnectPlatformSelectInput
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                      onDataChange={(ev) =>{
                                        handleInputChange( item.id , item.name, ev.target.value )
                                      }}
                                      options={[{label:'West US', value:'apiwest'},{label:'East US',value:"apieast" }]}
                                    />
                                  </div>
                                )
                              }
                            }

                            if(['GUNBROKER','ICONIC'].includes(data?.name ?? '')){
                              if(item.input_type=='select'){
                                return(
                                  <div key={item.id} className={`mb-3 justify-content-end`}>
                                    <ConnectPlatformSelectInput
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                      onDataChange={(ev) =>{
                                        handleInputChange( item.id , item.name, ev.target.value )
                                      }}
                                      options={[{label:'Sandbox', value:'SANDBOX'},{label:'Live',value:"LIVE" }]}
                                    />
                                  </div>
                                )
                              }
                            }

                              if (['ZALORA'].includes(data?.name ?? '')) {
                                if (item.input_type == 'select') {
                                  if (item.name === 'proxies') {
                                    return (
                                      <div key={item.id} className={`mb-3 row justify-content-end`}>
                                        <AuthRequirementSelectInput
                                          display_name={item.display_name}
                                          id={item.id}
                                          data={data}
                                          input_type={item.input_type}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                          onDataChange={(ev) => {
                                            handleInputChange(item.id, item.name, ev.target.value)
                                          }}
                                        />
                                      </div>
                                    )
                                  } else {
                                    return (
                                      <div key={item.id} className={`mb-3 row justify-content-end`}>
                                        <ConnectPlatformSelectInput
                                          display_name={item.display_name}
                                          id={item.id}
                                          input_type={item.input_type}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                          onDataChange={(ev) => {
                                            handleInputChange(item.id, item.name, ev.target.value)
                                          }}
                                          options={[
                                            {label: 'Sandbox', value: 'SANDBOX'},
                                            {label: 'Live', value: 'LIVE'},
                                          ]}
                                        />
                                      </div>
                                    )
                                  }
                                }
                              }

                            if(data?.name === 'FRUUGO'){
                              if(item.input_type=='select'){
                                return(
                                  <div key={item.id} className={`mb-3 row justify-content-end`}>
                                    <ConnectPlatformSelectInput
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                      onDataChange={(ev) =>{
                                        handleInputChange( item.id , item.name, ev.target.value )
                                      }}
                                      options={[{label:'GBP', value:'GBP'},{label:'INR',value:"INR" },{label:"USD",value:"USD"}]}
                                    />
                                  </div>
                                )
                              }
                            }

                            if(data?.name === 'SHOPIFY'){
                              if(item.name=='identifier'){
                                return(
                                <div key={item.id} className={`mb-3`}>
                                  <InputFieldComponent
                                    display_name={item.display_name}
                                    id={item.id}
                                    input_type={item.input_type}
                                    placeholder='eg. storename.myshopify.com'
                                    name={item.name}
                                    inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                    onDataChange={(inputValue) =>
                                      handleInputChange(inputValue.id, inputValue.name, inputValue.value)
                                    }
                                  />
                                </div>
                                )
                              }
                            }
      
                            if(data?.name === 'WOOCOMMERCE'){
                              if(item.name=="identifier"){
                                return(
                                  <div key={item.id} className={`mb-3`}>
                                    <InputFieldComponent
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      placeholder='Enter only domain eg. storename.mystore.com'
                                      name={item.name}
                                      inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                      onDataChange={(inputValue) =>
                                        handleInputChange(inputValue.id, inputValue.name, inputValue.value)
                                      }
                                    />
                                  </div>
                                )
                                
                              }
                            }

                            if(['DSCO','AMAZONSELLER','VIVINO','SHOPEE', 'ACUMATICA','WALMART','SQUARE','CDON','AMAZONMCF','AMAZONVENDOR','EMAG','TRADEME','NOON','NOONV2','CIN7OMNI','BOL','BACKMARKET'].includes(data?.name ?? '')){
                              if(item.input_type=='select'){
                                return(
                                  <div key={item.id} className={`mb-3`}>
                                    <AuthRequirementSelectInput
                                      display_name={item.display_name}
                                      id={item.id}
                                      data={data}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                      onDataChange={(ev) =>{
                                        handleInputChange( item.id , item.name, ev.target.value )
                                      }}
                                    />
                                  </div>
                                )
                              }
                            }

                            if (matchesPlatform(...currencyPlatforms, ...accountModePlatforms)) {
                              if (item.input_type == 'select') {
                                const normalizedName = (item.name || '').toLowerCase()
                                const normalizedDisplay = (item.display_name || '').toLowerCase()

                                if (
                                  normalizedName.includes('currency') ||
                                  normalizedDisplay.includes('currency')
                                ) {
                                  return (
                                    <div key={item.id} className={`mb-3`}>
                                      <AuthRequirementSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        data={data}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''}
                                        onDataChange={(ev) => {
                                          handleInputChange(item.id, item.name, ev.target.value)
                                        }}
                                      />
                                    </div>
                                  )
                                }

                                const hasAccount =
                                  normalizedName.includes('account') ||
                                  normalizedDisplay.includes('account')
                                const hasMode =
                                  normalizedName.includes('mode') ||
                                  normalizedDisplay.includes('mode')

                                if (hasAccount && hasMode) {
                                  return (
                                    <div key={item.id} className={`mb-3`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''}
                                        onDataChange={(ev) => {
                                          handleInputChange(item.id, item.name, ev.target.value)
                                        }}
                                        options={[
                                          {label: 'Sandbox', value: 'SANDBOX'},
                                          {label: 'Live', value: 'LIVE'},
                                        ]}
                                      />
                                    </div>
                                  )
                                }
                              }
                            }

                            if (item.input_type == 'select') {
                              return (
                                <div key={item.id} className={`mb-3`}>
                                  <AuthRequirementSelectInput
                                    display_name={item.display_name}
                                    id={item.id}
                                    data={data}
                                    input_type={item.input_type}
                                    name={item.name}
                                    inputValue={inputData[item.name] || ''}
                                    onDataChange={(ev) => {
                                      handleInputChange(item.id, item.name, ev.target.value)
                                    }}
                                  />
                                </div>
                              )
                            }

                            return (
                              <div key={item.id} className={`mb-3`}>
                                <InputFieldComponent
                                  display_name={item.display_name}
                                  id={item.id}
                                  input_type={item.input_type}
                                  name={item.name}
                                  inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                  onDataChange={(inputValue) =>
                                    handleInputChange(
                                      inputValue.id,
                                      inputValue.name,
                                      inputValue.value
                                    )
                                  }
                                />
                              </div>
                            )
                          }
                        )
                      : matchesPlatform('SHOPIFY', 'EBAY', 'ALLEGRO', 'ZohoInventory', 'TIKTOK','WOOCOMMERCE','TEMU','AMAZONSELLER','SHOPEE','GUNBROKER','LAZADA', 'ACUMATICA','WALMART','SQUARE','CDON','AMAZONMCF','AMAZONVENDOR','TRADEME','JUMIAVENDOR','LIGHTSPEED','VIRTUAL_STOCK')
                      ? visibleAuthRequirements.map(
                          (item: RequirementItem, index) => {
                            if (item.display_name === 'Shopify Domain') {
                              return (
                                <div key={item.id} className={`mb-3`}>
                                  <InputFieldComponent
                                    display_name={item.display_name}
                                    id={item.id}
                                    input_type={item.input_type}
                                    name={item.name}
                                    inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                    onDataChange={(inputValue) =>
                                      handleInputChange(
                                        inputValue.id,
                                        inputValue.name,
                                        inputValue.value
                                      )
                                    }
                                  />
                                </div>
                              )
                            } else {
                              if (['ZohoInventory','LAZADA', 'SHOPEE','JUMIAVENDOR'].includes(data?.name ?? '')) {
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3 row justify-content-end`}>
                                      <AuthRequirementSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        data={data}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                      />
                                    </div>
                                  )
                                }
                                return (
                                  <div key={item.id} className={`mb-3`}>
                                    <InputFieldComponent
                                      display_name={item.display_name}
                                      id={item.id}
                                      input_type={item.input_type}
                                      name={item.name}
                                      inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                      onDataChange={(inputValue) =>
                                        handleInputChange(
                                          inputValue.id,
                                          inputValue.name,
                                          inputValue.value
                                        )
                                      }
                                    />
                                  </div>
                                )
                              }

                              if (data?.name === 'TIKTOK') {
                                if (item.input_type == 'select') {
                                  return (
                                    <div key={item.id} className={`mb-3`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) => {
                                          handleInputChange(item.name, item.name, ev.target.value)
                                        }}
                                        options={[{label:'US', value:'US'},{label:'OTHERS',value:"OTHERS"}]}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if(data?.name === 'FRUUGO'){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3 row justify-content-end`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                        options={[{label:'GBP', value:'GBP'},{label:'INR',value:"INR" },{label:"USD",value:"USD"}]}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if(['WOOCOMMERCE', 'WALMART'].includes(data?.name ?? '')){
                                if(item.name=="identifier"){
                                  return(
                                    <div key={item.id} className={`mb-3`}>
                                      <InputFieldComponent
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        placeholder='Enter only domain eg. storename.mystore.com'
                                        name={item.name}
                                        inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                        onDataChange={(inputValue) =>
                                          handleInputChange(inputValue.id, inputValue.name, inputValue.value)
                                        }
                                      />
                                    </div>
                                  )
                                  
                                }
                              }

                              if(data?.name === 'AIMS360'){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                        options={[{label:'West US', value:'apiwest'},{label:'East US',value:"apieast" }]}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if (matchesPlatform(...currencyPlatforms, ...accountModePlatforms)) {
                                if (item.input_type == 'select') {
                                  const normalizedName = (item.name || '').toLowerCase()
                                  const normalizedDisplay = (item.display_name || '').toLowerCase()

                                  if (
                                    normalizedName.includes('currency') ||
                                    normalizedDisplay.includes('currency')
                                  ) {
                                    return (
                                      <div key={item.id} className={`mb-3`}>
                                        <AuthRequirementSelectInput
                                          display_name={item.display_name}
                                          id={item.id}
                                          data={data}
                                          input_type={item.input_type}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''}
                                          onDataChange={(ev) => {
                                            handleInputChange(item.id, item.name, ev.target.value)
                                          }}
                                        />
                                      </div>
                                    )
                                  }

                                  const hasAccount =
                                    normalizedName.includes('account') ||
                                    normalizedDisplay.includes('account')
                                  const hasMode =
                                    normalizedName.includes('mode') ||
                                    normalizedDisplay.includes('mode')

                                  if (hasAccount && hasMode) {
                                    return (
                                      <div key={item.id} className={`mb-3`}>
                                        <ConnectPlatformSelectInput
                                          display_name={item.display_name}
                                          id={item.id}
                                          input_type={item.input_type}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''}
                                          onDataChange={(ev) => {
                                            handleInputChange(item.id, item.name, ev.target.value)
                                          }}
                                          options={[
                                            {label: 'Sandbox', value: 'SANDBOX'},
                                            {label: 'Live', value: 'LIVE'},
                                          ]}
                                        />
                                      </div>
                                    )
                                  }
                                }
                              }

                              if(data?.name === 'VIRTUAL_STOCK'){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3 row justify-content-end`}>
                                      <AuthRequirementSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        data={data}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if(data?.name === 'GUNBROKER'){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3 row justify-content-end`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                        options={[{label:'Sandbox', value:'sandbox'},{label:'Live',value:"live" }]}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if(data?.name === 'TEMU'){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3`}>
                                      <ConnectPlatformSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.name , item.name, ev.target.value )
                                        }}
                                        options={[{label:'US', value:'US'},{label:'EU',value:"EU" }, { label: 'GLOBAL', value: "GLOBAL" }]}
                                      />
                                    </div>
                                  )
                                }
                              }

                              if(['AMAZONSELLER','VIVINO','SHOPEE','ACUMATICA','SQUARE','CDON','AMAZONMCF','AMAZONVENDOR','TRADEME','LIGHTSPEED', 'VIRTUAL_STOCK'].includes(data?.name ?? '')){
                                if(item.input_type=='select'){
                                  return(
                                    <div key={item.id} className={`mb-3`}>
                                      <AuthRequirementSelectInput
                                        display_name={item.display_name}
                                        id={item.id}
                                        data={data}
                                        input_type={item.input_type}
                                        name={item.name}
                                        inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                        onDataChange={(ev) =>{
                                          handleInputChange( item.id , item.name, ev.target.value )
                                        }}
                                      />
                                    </div>
                                  )
                                }
                             else{
                                return(
                                 <div key={item.id} className={`mb-3`}>
                                   <InputFieldComponent
                                      display_name={item.display_name}
                                     id={item.id}
                                     input_type={item.input_type}
                                     name={item.name}
                                     inputValue={inputData[item.id] || ''} // Pass the value from inputData
                                     onDataChange={(inputValue) =>
                                       handleInputChange(inputValue.id, inputValue.name, inputValue.value)
                                     }
                                   />
                                 </div>
                             )}
                              }

                            }
                            }
                          )
                        : null)}
                    {shouldRenderCurrencyFallback && (
                      <div className={`mb-3`}>
                        <AuthRequirementSelectInput
                          display_name='Currency'
                          id={-100}
                          data={data as Platform}
                          input_type='select'
                          name='currency'
                          inputValue={inputData['currency'] || ''}
                          onDataChange={(ev) => {
                            handleInputChange(-100, 'currency', ev.target.value)
                          }}
                        />
                      </div>
                    )}
                    {shouldRenderEbayCountrySelector && (
                      <div className={`mb-3`}>
                        <ConnectPlatformSelectInput
                          display_name='Select Country of your eBay Store'
                          id={-106}
                          input_type='select'
                          name='country_iso'
                          inputValue={inputData['country_iso'] || ''}
                          onDataChange={(ev) => {
                            handleInputChange('country_iso', 'country_iso', ev.target.value)
                          }}
                          options={countryNameOptions}
                        />
                      </div>
                    )}
                          {shouldRenderCloverRegion && (
                            <div className={`mb-3`}>
                              <ConnectPlatformSelectInput
                                display_name='Region'
                                id={-102}
                                input_type='select'
                                name='country_region'
                                inputValue={inputData['country_region'] || inputData['Region'] || ''}
                                onDataChange={(ev) => {
                                  handleInputChange('country_region', 'country_region', ev.target.value)
                                }}
                                options={cloverRegionOptions}
                              />
                            </div>
                          )}
                          {shouldRenderCountryFallback && (
                            <div className={`mb-3`}>
                              {matchesPlatform('FRUUGOV2') ? (
                                <ConnectPlatformSelectInput
                                  display_name='Country Name'
                                  id={-103}
                                  input_type='select'
                                  name='country'
                                  inputValue={inputData['country_code'] || ''}
                                  onDataChange={(ev) => {
                                    handleFruugoV2CountryChange(ev.target.value)
                                  }}
                                  options={countryNameOptions}
                                />
                              ) : (
                                <ConnectPlatformSelectInput
                                  display_name='Country'
                                  id={-103}
                                  input_type='select'
                                  name='country'
                                  inputValue={inputData['country'] || ''}
                                  onDataChange={(ev) => {
                                    handleInputChange('country', 'country', ev.target.value)
                                  }}
                                  options={noonV2CountryOptions}
                                />
                              )}
                            </div>
                          )}
                    {shouldRenderAccountModeFallback && (
                      <div className={`mb-3`}>
                        <ConnectPlatformSelectInput
                          display_name='Account Mode'
                          id={-101}
                          input_type='select'
                          name='account_mode'
                          inputValue={inputData['account_mode'] || ''}
                          onDataChange={(ev) => {
                            handleInputChange('account_mode', 'account_mode', ev.target.value)
                          }}
                          options={[
                            {label: 'Sandbox', value: 'SANDBOX'},
                            {label: 'Live', value: 'LIVE'},
                          ]}
                        />
                      </div>
                    )}
                    {matchesPlatform('LOGICBROKER') && (
                      <div className={`mb-3 row justify-content-end`}>
                        <div className='row'>
                          <div className='col-md-3 col-6'>
                            <label className='form-label'>Company</label>
                          </div>
                          <div className='col-sm-9 col-12'>
                            <input
                              type='text'
                              className='form-control form-control-solid'
                              readOnly
                              value={
                                logicBrokerPartnerLoading
                                  ? 'Fetching company...'
                                  : logicBrokerCompanyName || ''
                              }
                              placeholder='Company'
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
          {data && ['VEEQO'].includes(data?.name) && <small className='text-secondary fs-6'>Log in to Veeqo in another tab before connecting it here</small>}
              </>
            )}
          </div>
        
        <div className='d-flex justify-content-end mt-4' >
          <Button
            disabled={loading}
            className='btn-sm'
            variant='secondary'
          onClick={() => {
            closeModal()
            setInputData({
              friendlyName: '',
              intput_type: '',
              connection_type: '',
              country: '',
              country_code: '',
              country_iso: '',
              country_region: '',
              Region: '',
            })
            setLogicBrokerCompanyName('')
            setLogicBrokerPartnerId('')
            setLastLogicBrokerLookup({key: '', accountType: ''})
          }}
        >
            Close
          </Button>
          {!isShippHqAdminPortalOnly && (
          <button disabled={loading||platformSettingsLoading} className='btn btn-success btn-sm ms-2' type='submit' >{ loading||platformSettingsLoading ? 'Connecting...' : 'Connect'}</button>
          )}
        </div> </>)}
        </form>
      {/* </Modal> */}
    </>
  )
}

export default ConnectPlatformModal
